"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ClipboardList, Network, Anchor, ArrowRight } from "lucide-react";
const steps = [{
  id: "01",
  title: "Vessel Request",
  description: "Initial contact and requirement gathering. Submit your vessel's specific needs, from technical marine services to essential provisions and port logistics support.",
  icon: ClipboardList
}, {
  id: "02",
  title: "Supplier & Service Coordination",
  description: "We leverage our global maritime network to source high-quality supplies and coordinate specialized services from trusted, vetted vendors across our operational regions.",
  icon: Network
}, {
  id: "03",
  title: "Delivery & Service Execution",
  description: "Seamless logistics execution ensuring timely delivery to commercial vessels at designated ports, including the Panama Canal, Venezuela, Colombia, and Northern Spain.",
  icon: Anchor
}];
function AnimatedStepCard({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full rounded-none border-border/60 shadow-sm hover:shadow-md transition-shadow bg-card flex flex-col">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="size-12 bg-muted flex items-center justify-center border border-border/50">
              <step.icon className="size-6 text-primary" strokeWidth={1.5} />
            </div>
            <span className="text-4xl font-bold text-muted/50 select-none font-mono tracking-tighter">
              {step.id}
            </span>
          </div>
          <CardTitle className="text-xl font-serif tracking-tight text-foreground">
            {step.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-grow">
          <CardDescription className="text-base leading-relaxed text-muted-foreground">
            {step.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}
export default function HowOurCoordinationWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section id="howourcoordinationworks" className="py-20 md:py-32 bg-background relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-muted)_0%,transparent_40%)] opacity-50 pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-serif text-foreground mb-6 tracking-tight">
            How Our Coordination Works
          </h2>
          <Separator className="w-24 h-1 bg-ring mx-auto mb-6 rounded-none" />
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Venchandlers Nexus streamlines the complex process of maritime procurement. Our proven methodology ensures commercial vessels receive essential supplies and services efficiently across the Atlantic and Caribbean regions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 mb-16 md:mb-24">
          {steps.map((step, index) => <AnimatedStepCard key={step.id} step={step} index={index} />)}
        </div>

        <div ref={ctaRef} className={`text-center transition-all duration-700 delay-300 ease-out ${isCtaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Button asChild size="lg" className="bg-ring text-primary hover:bg-ring/90 rounded-none px-8 py-7 text-lg font-bold tracking-wide shadow-lg hover:shadow-xl transition-all group">
            <Link to="#request-quote-home">
              REQUEST FOR SERVICE
              <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground font-medium">
            Connect with our global maritime network today.
          </p>
        </div>
      </div>
    </section>;
}